import { getSubjectLabels } from '@/lib/settings';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { usePermission } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import {
  Download,
  FileText,
  Image as ImageIcon,
  Link as LinkIcon,
  Mail,
  Phone,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import LoeschenMitGrundDialog, { grundHeader } from '../../../components/ui/loeschen-mit-grund-dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { useStandort } from '../../../components/layouts/use-standort';
import { api, httpClient } from '../../../lib/api';

type ApplicationStatus = 'neu' | 'gesichtet' | 'im_gespraech' | 'abgesagt' | 'eingestellt';

const STATUS_LABELS: Record<ApplicationStatus, string> = {
  neu: 'Neu',
  gesichtet: 'Gesichtet',
  im_gespraech: 'Im Gespräch',
  abgesagt: 'Abgesagt',
  eingestellt: 'Eingestellt',
};

const STATUS_VARIANTS: Record<ApplicationStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  neu: 'default',
  gesichtet: 'secondary',
  im_gespraech: 'secondary',
  abgesagt: 'destructive',
  eingestellt: 'outline',
};

interface JobApplicationFile {
  id: number;
  original_filename: string;
  content_type: string;
  content_length: number;
}

interface JobApplication {
  id: number;
  standort_id: number;
  standort_name?: string | null;
  job_title?: string | null;
  job_type?: string | null;
  vorname: string;
  nachname: string;
  email: string;
  telefon?: string | null;
  faecher: string[];
  message?: string | null;
  links: string[];
  status: ApplicationStatus;
  internal_note?: string | null;
  landing_page?: string | null;
  notified_at?: string | null;
  notify_error?: string | null;
  created_at: string;
  updated_at: string;
  files: JobApplicationFile[];
}

interface JobApplicationListResponse {
  items: JobApplication[];
  total: number;
  limit: number;
  offset: number;
}

const formatDateTime = (value?: string | null) => {
  if (!value) return '–';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
};

const formatSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/**
 * Standort-Admin: eingegangene Coach-Bewerbungen auf die Standort-Jobs.
 * Unterlagen liegen im privaten S3-Bucket und werden nur über kurzlebige
 * signierte Links geöffnet.
 */
const StandortJobApplicationsPage: React.FC = () => {
  const { selectedStandortId, selectedStandort } = useStandort();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | 'alle'>('alle');
  const [search, setSearch] = useState('');
  // Nach Fach eingrenzen (T-0254) — aus den Fächern der geladenen Bewerbungen.
  const [fachFilter, setFachFilter] = useState('alle');
  const [selected, setSelected] = useState<JobApplication | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [zuLoeschen, setZuLoeschen] = useState<JobApplication | null>(null);
  const darfLoeschen = usePermission(P.recruiting.bewerbungen.bewerbung_loeschen);

  const load = useCallback(async () => {
    if (!selectedStandortId) return;
    setLoading(true);
    try {
      const params: Record<string, string> = { standort_id: String(selectedStandortId) };
      if (statusFilter !== 'alle') params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const data = await api.get<JobApplicationListResponse>('/api/job-applications', { params });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      console.error(e);
      toast.error('Bewerbungen konnten nicht geladen werden');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [selectedStandortId, statusFilter, search]);

  useEffect(() => {
    load();
  }, [load]);

  // Deep-Link aus der Benachrichtigungs-E-Mail: ?id=123 öffnet direkt das Detail.
  const deepLinkId = searchParams.get('id');
  useEffect(() => {
    if (!deepLinkId || !items.length) return;
    const match = items.find((item) => String(item.id) === deepLinkId);
    if (match) {
      setSelected(match);
      setNoteDraft(match.internal_note || '');
      searchParams.delete('id');
      setSearchParams(searchParams, { replace: true });
    }
  }, [deepLinkId, items, searchParams, setSearchParams]);

  const openDetail = (item: JobApplication) => {
    setSelected(item);
    setNoteDraft(item.internal_note || '');
  };

  const applyUpdate = async (
    item: JobApplication,
    payload: { status?: ApplicationStatus; internal_note?: string }
  ) => {
    setSaving(true);
    try {
      const updated = await api.put<JobApplication>(`/api/job-applications/${item.id}`, payload);
      setItems((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
      setSelected((prev) => (prev && prev.id === updated.id ? updated : prev));
      toast.success('Bewerbung aktualisiert');
    } catch (e) {
      console.error(e);
      toast.error('Aktualisierung fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const openFile = async (application: JobApplication, file: JobApplicationFile) => {
    try {
      const res = await api.post<{ url: string; expires_in: number; filename: string }>(
        `/api/job-applications/${application.id}/files/${file.id}/access-url`,
        {}
      );
      window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error(e);
      toast.error('Datei konnte nicht geöffnet werden');
    }
  };

  const removeApplication = async (item: JobApplication, grund: string) => {
    // DELETE liefert 204 ohne Body, deshalb httpClient statt api.delete.
    const res = await httpClient<unknown>(`/api/job-applications/${item.id}`, {
      method: 'DELETE',
      ...grundHeader(grund),
    } as RequestInit);
    if (res.error) throw new Error(res.error);
    setItems((prev) => prev.filter((row) => row.id !== item.id));
    setSelected(null);
    setZuLoeschen(null);
    toast.success('Bewerbung gelöscht');
  };

  const newCount = useMemo(() => items.filter((item) => item.status === 'neu').length, [items]);
  const faecher = useMemo(
    () => [...new Set(items.flatMap((item) => item.faecher || []))].sort((a, b) => a.localeCompare(b, 'de')),
    [items],
  );
  const sichtbar = useMemo(
    () => (fachFilter === 'alle' ? items : items.filter((item) => (item.faecher || []).includes(fachFilter))),
    [items, fachFilter],
  );

  if (!selectedStandortId) {
    return (
      <div className="mx-auto max-w-5xl p-4 sm:p-6">
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Bitte oben einen Standort auswählen, um dessen Bewerbungen zu sehen.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 sm:p-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle>Bewerbungen</CardTitle>
            <CardDescription>
              Eingegangene Bewerbungen für {selectedStandort?.name ?? 'diesen Standort'}
              {newCount > 0 ? ` · ${newCount} neu` : ''}
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input
              placeholder="Name oder E-Mail suchen …"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="sm:max-w-xs"
            />
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as ApplicationStatus | 'alle')}
            >
              <SelectTrigger className="sm:w-56">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle Status</SelectItem>
                {(Object.keys(STATUS_LABELS) as ApplicationStatus[]).map((value) => (
                  <SelectItem key={value} value={value}>
                    {STATUS_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {faecher.length > 0 ? (
              <Select value={fachFilter} onValueChange={setFachFilter}>
                <SelectTrigger className="sm:w-56" aria-label="Fach">
                  <SelectValue placeholder="Fach" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle Fächer</SelectItem>
                  {faecher.map((fach) => (
                    <SelectItem key={fach} value={fach}>
                      {fach}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
          </div>

          {sichtbar.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {loading
                ? 'Wird geladen …'
                : items.length > 0
                  ? 'Keine Bewerbung für dieses Fach.'
                  : 'Noch keine Bewerbungen eingegangen.'}
            </p>
          ) : (
            <ul className="divide-y rounded-lg border">
              {sichtbar.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => openDetail(item)}
                    className="flex w-full flex-col gap-1 p-4 text-left transition-colors hover:bg-muted/50"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">
                        {item.vorname} {item.nachname}
                      </span>
                      <Badge variant={STATUS_VARIANTS[item.status]}>
                        {STATUS_LABELS[item.status] ?? item.status}
                      </Badge>
                      {item.files.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {item.files.length} Datei{item.files.length === 1 ? '' : 'en'}
                        </span>
                      )}
                      {item.links.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {item.links.length} Link{item.links.length === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {item.job_title || 'Initiativbewerbung'}
                      {item.faecher.length > 0 ? ` · ${item.faecher.join(', ')}` : ''}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatDateTime(item.created_at)} · {item.email}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={selected !== null} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {selected.vorname} {selected.nachname}
                </DialogTitle>
                <DialogDescription>
                  {selected.job_title || 'Initiativbewerbung'}
                  {selected.job_type ? ` · ${selected.job_type}` : ''} ·{' '}
                  {formatDateTime(selected.created_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5 text-sm">
                <div className="grid gap-2 sm:grid-cols-2">
                  <a
                    href={`mailto:${selected.email}`}
                    className="flex items-center gap-2 text-primary hover:underline"
                  >
                    <Mail className="h-4 w-4" /> {selected.email}
                  </a>
                  {selected.telefon && (
                    <a
                      href={`tel:${selected.telefon}`}
                      className="flex items-center gap-2 text-primary hover:underline"
                    >
                      <Phone className="h-4 w-4" /> {selected.telefon}
                    </a>
                  )}
                </div>

                {selected.faecher.length > 0 && (
                  <div>
                    <p className="mb-1 font-medium">{getSubjectLabels().plural}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.faecher.map((fach) => (
                        <Badge key={fach} variant="secondary">
                          {fach}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selected.message && (
                  <div>
                    <p className="mb-1 font-medium">Nachricht</p>
                    <p className="whitespace-pre-wrap text-muted-foreground">{selected.message}</p>
                  </div>
                )}

                {selected.links.length > 0 && (
                  <div>
                    <p className="mb-1 font-medium">Links</p>
                    <ul className="space-y-1">
                      {selected.links.map((link) => (
                        <li key={link}>
                          <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="flex items-center gap-2 break-all text-primary hover:underline"
                          >
                            <LinkIcon className="h-4 w-4 shrink-0" /> {link}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selected.files.length > 0 && (
                  <div>
                    <p className="mb-1 font-medium">Unterlagen</p>
                    <ul className="space-y-2">
                      {selected.files.map((file) => (
                        <li
                          key={file.id}
                          className="flex items-center gap-3 rounded-md border px-3 py-2"
                        >
                          {file.content_type === 'application/pdf' ? (
                            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                          ) : (
                            <ImageIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                          )}
                          <span className="min-w-0 flex-1 truncate">{file.original_filename}</span>
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {formatSize(file.content_length)}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openFile(selected, file)}
                          >
                            <Download className="mr-2 h-4 w-4" /> Öffnen
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="grid gap-2">
                  <Label htmlFor="application-status">Status</Label>
                  <Select
                    value={selected.status}
                    onValueChange={(value) =>
                      applyUpdate(selected, { status: value as ApplicationStatus })
                    }
                  >
                    <SelectTrigger id="application-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(STATUS_LABELS) as ApplicationStatus[]).map((value) => (
                        <SelectItem key={value} value={value}>
                          {STATUS_LABELS[value]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="application-note">Interne Notiz</Label>
                  <Textarea
                    id="application-note"
                    rows={4}
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    placeholder="Nur intern sichtbar"
                  />
                </div>

                {selected.notify_error && (
                  <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                    Benachrichtigung an die Standort-E-Mail fehlgeschlagen: {selected.notify_error}
                  </p>
                )}
              </div>

              <DialogFooter className="gap-2 sm:justify-between">
                {/* Nur mit Recht — sonst lehnte erst der Server ab (T-0215). */}
                {darfLoeschen ? (
                  <Button
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setZuLoeschen(selected)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Löschen
                  </Button>
                ) : (
                  <span />
                )}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setSelected(null)}>
                    Schließen
                  </Button>
                  <Button
                    disabled={saving || noteDraft === (selected.internal_note || '')}
                    onClick={() => applyUpdate(selected, { internal_note: noteDraft })}
                  >
                    Notiz speichern
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      <LoeschenMitGrundDialog
        offen={zuLoeschen !== null}
        titel={`Bewerbung von ${zuLoeschen?.vorname ?? ''} ${zuLoeschen?.nachname ?? ''} löschen?`}
        onAbbrechen={() => setZuLoeschen(null)}
        onLoeschen={async (grund) => {
          if (zuLoeschen) await removeApplication(zuLoeschen, grund);
        }}
      />
    </div>
  );
};

export default StandortJobApplicationsPage;
